import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { collection, addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAuth } from '../../../contexts/auth.context';

// ─── Types ───────────────────────────────────────────────────────────────────

type ActivityType = 'call' | 'email' | 'meeting' | 'note' | 'task';

const ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  { value: 'call', label: 'Llamada' },
  { value: 'email', label: 'Correo' },
  { value: 'meeting', label: 'Reunión' },
  { value: 'note', label: 'Nota' },
  { value: 'task', label: 'Tarea' },
];

// ─── Props ───────────────────────────────────────────────────────────────────

interface RegisterActivityDialogProps {
  open: boolean;
  personId: string;
  onClose: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function RegisterActivityDialog({ open, personId, onClose }: RegisterActivityDialogProps) {
  const { currentTenantId } = useTenantStore();
  const { user } = useAuth();

  const [type, setType] = useState<ActivityType>('note');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (saving) return;
    setType('note');
    setSubject('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!notes.trim()) {
      setError('Las notas son obligatorias.');
      return;
    }
    if (!currentTenantId || !user) {
      setError('No hay sesión activa.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Resolve the CRM contact for this person (optional — activity is still useful without it)
      let contactId = 'unknown';
      try {
        const contactsSnap = await getDocs(
          query(
            collection(db, `tenants/${currentTenantId}/crmContacts`),
            where('linkedPersonId', '==', personId),
          ),
        );
        if (!contactsSnap.empty) {
          contactId = contactsSnap.docs[0]?.id ?? 'unknown';
        }
      } catch {
        // Non-fatal: proceed without contactId
      }

      await addDoc(collection(db, `tenants/${currentTenantId}/crmActivities`), {
        tenantId: currentTenantId,
        contactId,
        linkedPersonId: personId,
        type,
        subject: subject.trim() || null,
        notes: notes.trim(),
        occurredAt: Timestamp.now(),
        createdAt: Timestamp.now(),
        createdBy: user.uid,
      });

      handleClose();
    } catch (err: any) {
      console.error('[RegisterActivityDialog] Save failed:', err);
      setError('No se pudo guardar la actividad. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Registrar Actividad
          </Typography>
          <Tooltip title="Cerrar">
            <IconButton size="small" onClick={handleClose} disabled={saving}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Tipo de actividad"
            value={type}
            onChange={(e) => setType(e.target.value as ActivityType)}
            size="small"
            fullWidth
          >
            {ACTIVITY_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Asunto (opcional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            size="small"
            fullWidth
            placeholder="ej. Llamada de seguimiento"
          />

          <TextField
            label="Notas *"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={4}
            placeholder="Describe la interacción o actividad..."
            error={!!error && !notes.trim()}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={saving} size="small">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !notes.trim()}
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? 'Guardando…' : 'Guardar Actividad'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
